"use client";

import { API_ENDPOINT } from "@cede/utils";
import { isPerpsExchange } from "./config";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

/**
 * Highest leverage the account can actually set on a market, from the dedicated endpoint.
 * On Binance leverage brackets are per-account, so the static per-market/config ceiling can
 * exceed what the exchange accepts (setLeverage then fails with -4028). `null`/`undefined`
 * means the venue exposes no ceiling — consumers fall back to market/config values.
 */
export function useCexPerpsMaxLeverage(
  exchangeId: string,
  accountId: string | undefined,
  pairSymbol: string | undefined,
) {
  const { auth } = useHubUser();
  const apiHelper = useApi();

  const { data } = useQuery({
    queryKey: ["cex-perps-max-leverage", exchangeId, accountId, pairSymbol],
    queryFn: async (): Promise<number | null> => {
      const { maxLeverage } = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_TRADE_MAX_LEVERAGE, {
        queryParams: { accountId: accountId!.toString(), pairSymbol: pairSymbol! },
      });
      return maxLeverage;
    },
    enabled: isPerpsExchange(exchangeId) && !!accountId && !!pairSymbol && !!auth.identityToken,
    staleTime: 60 * 60_000,
  });

  return data;
}
